/**
 * MessageThread - Scrollable list of message bubbles
 *
 * Auto-scrolls to bottom when new messages arrive.
 * Shows a loading placeholder when the agent is responding.
 */

import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { SmartToy as SmartToyIcon } from '@mui/icons-material';
import MessageBubble from './MessageBubble';

/**
 * MessageThread Component
 *
 * @param {Object} props
 * @param {Array} props.messages - Array of message objects
 * @param {boolean} props.isAgentResponding - Whether agent is currently responding
 * @param {Function} props.onArtifactClick - Artifact chip click handler
 * @param {Array}  [props.suggestions=[]] - All outstanding/resolved suggestion docs
 * @param {Function} [props.onApprove] - (suggestionId) => void
 * @param {Function} [props.onDismiss] - (suggestionId) => void
 * @param {Function} [props.onModify] - (suggestionId, note) => void
 * @param {boolean} [props.canApprove=true] - Whether the current user can approve proposals
 */
const MessageThread = ({
  messages = [],
  isAgentResponding = false,
  onArtifactClick,
  suggestions = [],
  onApprove,
  onDismiss,
  onModify,
  canApprove = true
}) => {
  const bottomRef = useRef(null);

  // Auto-scroll to bottom when messages change or agent starts/stops responding
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isAgentResponding]);

  if (messages.length === 0 && !isAgentResponding) {
    return (
      <Box
        sx={{
          // Grid row 2 (1fr) — fills all available space between ChatHeader and TripContextDrawer.
          // overflow: hidden keeps the grid row bounded; inner flex centres the placeholder.
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
          gap: 2,
          p: 3
        }}
      >
        <SmartToyIcon sx={{ fontSize: '3rem', opacity: 0.3 }} />
        <Typography variant="body2" textAlign="center" sx={{ maxWidth: 300 }}>
          Start a conversation with your travel assistant. Ask about your trip, get recommendations,
          or explore destination information.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        // Grid row 2 (1fr) — fills remaining space; overflow-y: auto scrolls messages.
        overflowY: 'auto',
        p: 2,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {messages.map((message) => (
        <MessageBubble
          key={message.id}
          message={message}
          onArtifactClick={onArtifactClick}
          suggestions={suggestions}
          onApprove={onApprove}
          onDismiss={onDismiss}
          onModify={onModify}
          canApprove={canApprove}
        />
      ))}

      {/* Streaming placeholder when agent is responding */}
      {isAgentResponding && (
        <MessageBubble
          message={{
            id: '__streaming__',
            role: 'agent',
            content: '',
            timestamp: null,
            metadata: {}
          }}
          onArtifactClick={onArtifactClick}
          isStreaming
        />
      )}

      {/* Scroll anchor */}
      <div ref={bottomRef} />
    </Box>
  );
};

export default MessageThread;
